import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, FormControl, Grid, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetError, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { addChangesHistory } from 'store/ticket-service/asyncActions';
import { getActiveTicket, getTicketUpdating } from 'store/ticket-service/selector';
import {
	setEditMode,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { setIsOpenNoDeleteAccess, triggerTicketSave } from 'store/user-service/userSlice';

import {
	HistoryActionTypes,
	NamespacesList,
	SharePermissions,
	TicketStages,
} from 'core/enums/common';
import { editNotificationsSchema, editTitleSchema } from 'core/helpers/yupSchemas';

import { FormTitleValue } from 'pages/Home';

import { IFormattedNotification } from 'components/TicketSteps/NotificationStep';
import { CustomSwitch } from 'components/shared/CustomSwitch';
import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { ReactComponent as Refresh } from 'assets/icons/refresh.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

interface IFormNotification {
	title?: string;
	description?: string;
	sms: boolean;
	email: boolean;
	push: boolean;
	isDeleted: boolean;
}

interface FormValues {
	notifications: IFormNotification[];
}

interface EditTicketFormProps {
	notificationsObj: Array<IFormattedNotification>;
	setNotifications: (notifications: Array<IFormattedNotification>) => void;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

export const EditNotificationsStep: FC<EditTicketFormProps> = ({
	notificationsObj,
	setNotifications,
	ticketTitle,
	setTitleError,
}) => {
	const { t: notificationsTranslate } = useTranslation(NamespacesList['notificationsStep']);
	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const activeTicket = useSelector(getActiveTicket);
	const updating = useSelector(getTicketUpdating);

	const [initialData, setInitialData] = useState<FormValues>({
		notifications: [],
	});

	const {
		control,
		handleSubmit,
		register,
		reset,
		formState: { errors },
		getValues,
	} = useForm<FormValues>({
		resolver: yupResolver(editNotificationsSchema()),
		defaultValues: {
			notifications: [],
		},
	});

	const { fields: notifications } = useFieldArray({
		control,
		name: 'notifications',
	});

	useEffect(() => {
		if (activeTicket) {
			const updatedNotifications = activeTicket.notifications.map(
				(notification): IFormNotification => ({
					...notification,
					isDeleted: false,
				})
			);

			setInitialData({ notifications: updatedNotifications });

			reset({
				notifications: activeTicket.notifications || [],
			});
		}
	}, [activeTicket?.id]);

	const currentStepPermission = useMemo(() => {
		const currentStagePermission = activeTicket?.permissions?.find(
			(permission) =>
				permission.ticketCurrentStep === TicketStages.notifications ||
				permission.ticketCurrentStep === null
		);

		if (currentStagePermission) return currentStagePermission.permission;

		return null;
	}, [activeTicket?.id, activeTicket?.permissions]);

	const handleDeleteNotification = (index: number) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			const updatedNotifications = getValues().notifications;
			updatedNotifications[index].isDeleted = true;

			reset({
				...getValues(),
				notifications: updatedNotifications,
			});
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	const handleRefreshNotification = (index: number) => {
		const updatedNotifications = getValues().notifications;
		updatedNotifications[index].isDeleted = false;

		reset({
			...getValues(),
			notifications: updatedNotifications,
		});
	};

	const updateTicketInfo = (data: FormValues) => {
		const { notifications } = data;

		if (activeTicket) {
			const id = activeTicket.id;

			const filteredNotifications: Array<IFormNotification> = notifications.filter(
				(notification) => !notification.isDeleted && notification.title && notification.description
			);

			editTitleSchema()
				.validate({ title: ticketTitle })
				.then((res) => {
					if (res) {
						dispatch(updateLocalTicket({ id, title: ticketTitle, filteredNotifications }));
						saveTicketChanges();
					}
				})
				.catch((error) => {
					setTitleError('title', {
						type: 'manual',
						message: error.message,
					});
				});

			const formattedNotifications = filteredNotifications.map(
				(notification: Record<string, any>) => ({
					title: notification.title,
					description: notification.description,
					permissions: {
						Email: notification.email,
						Push: notification.push,
						SMS: notification.sms,
					},
					isDeleted: notification.isDeleted,
				})
			);

			setNotifications(formattedNotifications);

			handleTicketChanges({
				oldData: initialData,
				newData: {
					notifications: notifications,
				},
			});
		}
	};

	const handleResetEditForm = useCallback(() => {
		reset();
		dispatch(setEditMode(false));
	}, [reset]);

	const handleTicketChanges = ({
		oldData,
		newData,
	}: {
		oldData: FormValues;
		newData: FormValues;
	}) => {
		if (activeTicket) {
			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: activeTicket.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.notificationsEdit,
					oldValue: JSON.stringify({
						notifications: oldData.notifications,
					}),
					newValue: JSON.stringify({
						notifications: newData.notifications,
					}),
				})
			);
		}
	};

	const saveTicketChanges = () => {
		dispatch(setIsTicketUpdate(true));
		dispatch(triggerTicketSave());
		dispatch(setEditMode(false));
	};

	return (
		<form
			className="rtl-able"
			style={{
				width: '100%',
				height: '100%',
				display: 'grid',
				gridTemplateRows: 'auto 0fr',
				overflow: 'auto',
			}}
			onSubmit={handleSubmit(updateTicketInfo)}
		>
			<Grid
				container
				direction="column"
				sx={{
					flexWrap: 'nowrap',
					overflowY: 'auto',
					height: 'calc(100% - 4px)',
					padding: i18n?.language === 'ar' ? '0 0 0 12px' : '0 12px 0 0',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						width: '90%',
						marginBottom: '16px',
					}}
				>
					<Typography sx={{ fontWeight: '700', color: titleTextColor }}>
						{notificationsTranslate('generalText.sidePanelTitle')}
					</Typography>
					<Typography sx={{ color: regularTextColor }}>
						{notificationsTranslate('generalText.sidePanelDescription')}
					</Typography>
				</Box>
				<Grid item sx={{ height: '100%' }}>
					<FormControl fullWidth sx={{ height: '100%' }}>
						{notifications.map((notification, index) => (
							<Box key={notification.id}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										margin: '24px 0',
										gap: '12px',
										position: 'relative',
									}}
								>
									{!notification.isDeleted ? (
										<Button
											type="button"
											sx={{
												position: 'absolute',
												top: '-20px',
												right: i18n?.language === 'ar' ? 'unset' : '2px',
												left: i18n?.language === 'ar' ? '2px' : 'unset',
												minWidth: '24px',
												height: '24px',
												fontSize: '24px',
												color: '#9FA6B3',
											}}
											onClick={() => handleDeleteNotification(index)}
										>
											<span>&times;</span>
										</Button>
									) : (
										<Button
											type="button"
											sx={{
												position: 'absolute',
												top: '-20px',
												right: i18n?.language === 'ar' ? 'unset' : '2px',
												left: i18n?.language === 'ar' ? '2px' : 'unset',
												minWidth: '24px',
												height: '24px',
												fontSize: '24px',
												color: '#9FA6B3',
											}}
											onClick={() => handleRefreshNotification(index)}
										>
											<Refresh />
										</Button>
									)}

									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-between',
											gap: '10px',
											width: '80%',
										}}
									>
										<ExpandableTextField
											errors={errors?.['notifications']?.[index]?.['title']}
											registerConfig={register(`notifications.${index}.title`)}
											defaultValue={notification?.title || ''}
											isDisabled={notification.isDeleted}
										/>

										<ExpandableTextField
											errors={errors?.['notifications']?.[index]?.['description']}
											registerConfig={register(`notifications.${index}.description`)}
											defaultValue={notification?.description || ''}
											isDisabled={notification.isDeleted}
										/>
									</Box>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-evenly',
										}}
									>
										{notificationsObj[index]?.permissions
											? Object.entries(notificationsObj[index]?.permissions).map(
													([permission, value]: [string, boolean], permissionIndex) => (
														<Box
															key={`${index * 3 + permissionIndex}${permission}`}
															sx={{
																width: '100%',
																display: 'flex',
																gap: '8px',
																marginRight: '5px',
																flexDirection: i18n?.language === 'ar' ? 'row-reverse' : 'row',
																justifyContent: 'space-between',
															}}
														>
															<CustomSwitch
																id={`${index * 3 + permissionIndex}${permission}`}
																disabled={true}
																checked={value}
																onChange={() => {}}
															/>
															<Typography
																sx={{ fontSize: '14px', fontWeight: '700', whiteSpace: 'nowrap' }}
															>
																{notificationsTranslate(`notifications.${permission}`)}
															</Typography>
														</Box>
													)
											  )
											: null}
									</Box>
								</Box>

								<Divider />
							</Box>
						))}
					</FormControl>
				</Grid>
			</Grid>

			<Box
				className="not-rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					marginTop: 'auto',
				}}
			>
				<Button
					sx={{
						marginTop: '16px',
						height: '44px',
						width: '133px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
					}}
					variant="outlined"
					color="primary"
					onClick={handleResetEditForm}
				>
					{commonTranslate('buttons.cancel')}
				</Button>

				{updating ? (
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
						}}
						type="submit"
						variant="contained"
						color="primary"
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Button>
				) : (
					<>
						{notifications.length ? (
							<Button
								sx={{
									marginTop: '16px',
									height: '44px',
									width: '133px',
									borderRadius: '8px',
									textTransform: 'none',
									fontSize: '16px',
									fontWeight: '700',
								}}
								type="submit"
								variant="contained"
								color="primary"
								onClick={() => dispatch(setIsTicketUpdate(true))}
							>
								{commonTranslate('buttons.save')}
							</Button>
						) : null}
					</>
				)}
			</Box>
		</form>
	);
};
